import { useCallback } from 'react';
import { prop } from 'ramda';
import { dispatcher } from 'dispatcher';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'js/store/hooks';
import { IUserDetails } from 'js/hooks/user/store/types';
import { userSetAttributesAction } from 'js/hooks/user/store/actions';
import { pushDispatcher } from 'js/hooks/socket/hooks/usePushSubscription';
import {
    useFetchBalance,
    useFetchUserDetails,
    useGetUserUrlTemplates
} from 'js/hooks/user';

const useVerifyJumio = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const netVerifyJumioEnabled = useAppSelector(prop('netVerifyJumioEnabled'));

    return useCallback(({ suspended, ageVerificationStatus }: IUserDetails) => {
        const params = new URLSearchParams(location.search);
        const isHideKYC = params.get('hideKYC') === '1';
        const isUserSuspended = suspended === 'true';
        const isJumioAllowed = netVerifyJumioEnabled && ageVerificationStatus === 'inprocess';

        if (isJumioAllowed && !isHideKYC) {
            dispatch(userSetAttributesAction({ suspended: isUserSuspended }));

            pushDispatcher.trigger('userVerify', isUserSuspended);

            if (isUserSuspended) {
                dispatcher.dispatch('auth:suspend', true);

                return Promise.reject(false);
            }

            dispatcher.dispatch('auth:suspend', false);
        }

        return true;
    }, [location, netVerifyJumioEnabled, dispatch]);
};

export const useAfterLoginAction = () => {
    const verifyJumio = useVerifyJumio();
    const getUserUrlTemplates = useGetUserUrlTemplates();
    const fetchUserDetails = useFetchUserDetails();
    const fetchBalance = useFetchBalance();

    return useCallback(({ signal }: { signal?: AbortSignal } = {}) => Promise.all([
        fetchUserDetails([], { signal }).then(verifyJumio),
        getUserUrlTemplates({ signal }),
        fetchBalance({ signal })
    ]), [
        fetchBalance,
        fetchUserDetails,
        getUserUrlTemplates,
        verifyJumio
    ]);
};
